import { motion } from "framer-motion";
import { BarChart, Waves, Zap } from "lucide-react";
import React from "react";
import { FeedbackPoint, TrackAnalysis as HapticTrackAnalysis } from "../hooks/useHapticsEngine/types";

interface TrackAnalysisProps {
  hapticAnalysis?: HapticTrackAnalysis;
}

export const TrackAnalysis: React.FC<TrackAnalysisProps> = ({ hapticAnalysis }) => {
  if (!hapticAnalysis) {
    return <div className="text-zinc-400 flex items-center justify-center p-6">No haptic analysis data available</div>;
  }

  // Calculate energy distribution
  const calculateEnergyDistribution = (feedbackPoints: FeedbackPoint[]) => {
    const intensityBuckets = [0, 0.25, 0.5, 0.75, 1].map(
      (threshold) =>
        feedbackPoints.filter((point) => point.intensity >= threshold && point.intensity < threshold + 0.25).length
    );
    return intensityBuckets;
  };

  // Calculate frequency distribution
  const calculateFrequencyDistribution = (feedbackPoints: FeedbackPoint[]) => {
    const frequencyRanges = [
      { label: "Low", min: 0, max: 100 },
      { label: "Mid-Low", min: 100, max: 250 },
      { label: "Mid", min: 250, max: 500 },
      { label: "Mid-High", min: 500, max: 1000 },
      { label: "High", min: 1000, max: Infinity },
    ];

    return frequencyRanges.map(
      (range) => feedbackPoints.filter((point) => point.frequency >= range.min && point.frequency < range.max).length
    );
  };

  const energyDistribution = calculateEnergyDistribution(hapticAnalysis.feedbackPoints);
  const frequencyDistribution = calculateFrequencyDistribution(hapticAnalysis.feedbackPoints);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-800/50 rounded-lg p-6 space-y-6"
    >
      <div className="flex items-center gap-3 mb-4">
        <Zap className="w-6 h-6 text-yellow-500" />
        <h2 className="text-xl font-bold">Haptic Analysis</h2>
      </div>

      {/* Energy Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Waves className="w-5 h-5 text-yellow-500" />
            <span className="text-zinc-400 text-sm">Overall Energy</span>
          </div>
          <div className="text-2xl font-bold text-yellow-500">{hapticAnalysis.energy.toFixed(2)}</div>
        </div>

        <div className="bg-zinc-900/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart className="w-5 h-5 text-yellow-500" />
            <span className="text-zinc-400 text-sm">Feedback Points</span>
          </div>
          <div className="text-2xl font-bold text-yellow-500">{hapticAnalysis.feedbackPoints.length}</div>
        </div>

        <div className="bg-zinc-900/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Waves className="w-5 h-5 text-yellow-500" />
            <span className="text-zinc-400 text-sm">Max Intensity</span>
          </div>
          <div className="text-2xl font-bold text-yellow-500">
            {Math.max(...hapticAnalysis.feedbackPoints.map((p) => p.intensity)).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Energy Distribution */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-semibold text-zinc-300">Energy Distribution</h3>
        </div>
        <div className="flex items-end h-24 gap-2">
          {energyDistribution.map((count, index) => (
            <div
              key={index}
              className="flex-1 bg-yellow-500/30 hover:bg-yellow-500/50 transition-all"
              style={{
                height: `${(count / Math.max(...energyDistribution)) * 100}%`,
                minHeight: "10px",
              }}
              title={`Intensity Range ${index * 0.25} - ${(index + 1) * 0.25}: ${count} points`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-zinc-400 mt-2">
          {["0-0.25", "0.25-0.5", "0.5-0.75", "0.75-1"]}
        </div>
      </div>

      {/* Frequency Distribution */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-4">
          <Waves className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-semibold text-zinc-300">Frequency Distribution</h3>
        </div>
        <div className="flex items-end h-24 gap-2">
          {frequencyDistribution.map((count, index) => (
            <div
              key={index}
              className="flex-1 bg-yellow-500/30 hover:bg-yellow-500/50 transition-all"
              style={{
                height: `${(count / Math.max(...frequencyDistribution)) * 100}%`,
                minHeight: "10px",
              }}
              title={`Frequency Range: ${count} points`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-zinc-400 mt-2">
          {["Low", "Mid-Low", "Mid", "Mid-High", "High"]}
        </div>
      </div>
    </motion.div>
  );
};
